// src/layouts/SharedComponents/AvatarCropModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useRef, useCallback, useEffect } from 'react';
import { Button, Modal, Space } from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import translateX from 'utils/translateX';
import attachmentsService from 'services/attachmentsService';
import appService from 'services/appService';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CROP_BORDER_RADIUS = 10; // 0.625rem = 10px
const MIN_CROP_SIZE = 50;
const OUTPUT_SIZE = 512; // Final image size (high quality, will be displayed at 150x150)
const ASPECT_RATIO = 1; // Square crop (1:1 ratio for 150x150 display)

// *******************************************************************************
// HELPER FUNCTIONS
// *******************************************************************************

/**
 * Center a crop with a given aspect ratio
 */
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 80,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

/**
 * Get cropped image as a blob
 * @param {HTMLImageElement} image - The image element
 * @param {Object} crop - Crop coordinates
 * @param {string} mimeType - Output mime type (image/png or image/jpeg)
 */
function getCroppedImg(image, crop, mimeType = 'image/jpeg') {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('No 2d context'));
      return;
    }

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Calculate actual crop dimensions in natural pixels
    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;

    // Set canvas size to output size (square)
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;

    // For PNG, keep transparency. For JPEG, fill with white background
    if (mimeType === 'image/jpeg') {
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    }

    // Draw cropped image
    ctx.drawImage(image, cropX, cropY, cropWidth, cropHeight, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

    const quality = mimeType === 'image/png' ? undefined : 0.9;
    canvas.toBlob(
      blob => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      quality
    );
  });
}

/**
 * Generate base64 thumbnail from blob
 * @param {Blob} blob - Image blob
 * @param {number} size - Thumbnail size
 * @param {string} mimeType - Output mime type
 */
async function generateBase64Thumb(blob, size = 128, mimeType = 'image/jpeg') {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // For JPEG, fill with white background
        if (mimeType === 'image/jpeg') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, size, size);
        }

        ctx.drawImage(img, 0, 0, size, size);
        const quality = mimeType === 'image/png' ? undefined : 0.7;
        resolve(canvas.toDataURL(mimeType, quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Detect if image has transparency by checking file type
 */
function getImageMimeType(file) {
  if (file?.type === 'image/png') return 'image/png';
  if (file?.type === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

/**
 * Get file extension from mime type
 */
function getExtensionFromMime(mimeType) {
  if (mimeType === 'image/png') return 'png';
  if (mimeType === 'image/webp') return 'webp';
  return 'jpg';
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const AvatarCropModal = ({ open, onCancel, onConfirm, imageFile, onRetakePhoto }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  const imgRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // Load image when file changes
  useEffect(() => {
    if (imageFile && open) {
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setCrop(undefined);
        setCompletedCrop(null);
      };
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile, open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setImageSrc(null);
      setCrop(undefined);
      setCompletedCrop(null);
    }
  }, [open]);

  // Calculate completedCrop when crop is set and image is loaded
  // This ensures the initial crop area is captured even without user interaction
  useEffect(() => {
    if (crop && imgRef.current && !completedCrop) {
      const { width, height } = imgRef.current;
      if (width > 0 && height > 0 && crop.unit === '%') {
        const pixelCrop = {
          unit: 'px',
          x: Math.round((crop.x / 100) * width),
          y: Math.round((crop.y / 100) * height),
          width: Math.round((crop.width / 100) * width),
          height: Math.round((crop.height / 100) * height),
        };
        setCompletedCrop(pixelCrop);
      }
    }
  }, [crop, completedCrop]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  const handleConfirm = async () => {
    if (!imgRef.current || !completedCrop || !onConfirm) return;

    setUploading(true);

    try {
      // Detect mime type from original file (preserve PNG transparency)
      const mimeType = getImageMimeType(imageFile);
      const extension = getExtensionFromMime(mimeType);

      // Get cropped image blob
      const croppedBlob = await getCroppedImg(imgRef.current, completedCrop, mimeType);

      // Generate base64 thumbnail for immediate preview
      const base64Thumb = await generateBase64Thumb(croppedBlob, 128, mimeType);

      // Immediately show preview
      onConfirm({
        previewUrl: base64Thumb,
        isUploading: true,
      });

      // Create file from blob
      const filename = `avatar-${Date.now()}.${extension}`;
      const file = new File([croppedBlob], filename, { type: mimeType });

      // Upload using attachments service
      const result = await attachmentsService.upload({
        file,
        checkDuplicate: false,
        generateThumb: true,
      });

      if (result?.attachment) {
        const { storageUrl, base64Thumb: serverThumb, _id: attachmentId } = result.attachment;

        // Update with final data
        onConfirm({
          largeUrl: storageUrl,
          thumbUrl: storageUrl,
          base64Thumb: serverThumb || base64Thumb,
          attachmentId,
          isUploading: false,
        });

        appService.message({ flag: 's', text: translateX('image_uploaded_successfully') });
      } else {
        // Upload failed - reset loading state
        onConfirm({ isUploading: false });
        appService.message({ flag: 'e', text: translateX('image_upload_failed') });
      }
    } catch (error) {
      console.error('Error uploading cropped image:', error);
      // Upload failed - reset loading state
      onConfirm({ isUploading: false });
      appService.message({ flag: 'e', text: translateX('image_upload_failed') });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const onImageLoad = useCallback(e => {
    const img = e.currentTarget;
    imgRef.current = img;
    const { width, height } = img;

    if (width === 0 || height === 0) return;

    // Create centered crop (returns percentage-based crop)
    // The useEffect will handle converting to pixel crop for completedCrop
    const percentCrop = centerAspectCrop(width, height, ASPECT_RATIO);
    setCrop(percentCrop);
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────

  // Custom footer with "Retake Photo" button when onRetakePhoto is provided
  const modalFooter = onRetakePhoto ? (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Button icon={<CameraOutlined />} onClick={onRetakePhoto} disabled={uploading}>
        {translateX('retake_photo')}
      </Button>
      <Space>
        <Button onClick={handleCancel} disabled={uploading}>
          {translateX('cancel')}
        </Button>
        <Button
          type="primary"
          onClick={handleConfirm}
          disabled={!completedCrop || uploading}
          loading={uploading}
        >
          {translateX('confirm')}
        </Button>
      </Space>
    </div>
  ) : undefined;

  return (
    <Modal
      title={translateX('crop_image')}
      open={open}
      onCancel={handleCancel}
      onOk={onRetakePhoto ? undefined : handleConfirm}
      okText={onRetakePhoto ? undefined : translateX('confirm')}
      cancelText={onRetakePhoto ? undefined : translateX('cancel')}
      okButtonProps={onRetakePhoto ? undefined : { disabled: !completedCrop || uploading, loading: uploading }}
      footer={modalFooter}
      width={520}
      centered
      destroyOnHidden
      mask={{ closable: false }}
    >
      {/* Crop area */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          minHeight: 300,
          background: '#1a1a1a',
          borderRadius: 8,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        {imageSrc && (
          <ReactCrop
            crop={crop}
            onChange={(pixelCrop, percentCrop) => {
              setCrop(percentCrop);
              // Also update completedCrop on every change to keep them in sync
              setCompletedCrop(pixelCrop);
            }}
            onComplete={pixelCrop => setCompletedCrop(pixelCrop)}
            aspect={ASPECT_RATIO}
            minWidth={MIN_CROP_SIZE}
            minHeight={MIN_CROP_SIZE}
            circularCrop={false}
            ruleOfThirds
          >
            <img
              ref={imgRef}
              src={imageSrc}
              alt="Crop"
              onLoad={onImageLoad}
              style={{
                maxHeight: 400,
                maxWidth: '100%',
              }}
            />
          </ReactCrop>
        )}
      </div>

      {/* Custom styles for rounded crop area */}
      <style>{`
        .ReactCrop__crop-selection {
          border-radius: ${CROP_BORDER_RADIUS}px !important;
        }
        .ReactCrop__drag-handle {
          border-radius: 50% !important;
        }
      `}</style>
    </Modal>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default AvatarCropModal;
